import { getTokenEndpoint } from '../../config';

// Link problems a retry cannot fix (invalidLink, expiredLink, callEnded) replace the join card;
// 'unavailable' (network/server errors) is shown in the card and the user can retry.
export type JoinErrorKind = 'invalidLink' | 'expiredLink' | 'callEnded' | 'unavailable';

export class JoinError extends Error {
  kind: JoinErrorKind;

  constructor(kind: JoinErrorKind) {
    super(`Unable to join the video call: ${kind}`);
    this.name = 'JoinError';
    this.kind = kind;
  }
}

const STATUS_ERRORS: { [status: number]: JoinErrorKind } = {
  400: 'invalidLink',
  401: 'expiredLink',
  410: 'callEnded',
};

const JWT_PATTERN = /^[\w-]+\.[\w-]+\.[\w-]+$/;

// The `ct` query parameter carries the call token from the meeting link (app or SMS).
export function getCallTokenParam() {
  return new window.URLSearchParams(window.location.search).get('ct');
}

// Exchanges the link's call token for a Twilio access token (JWT).
export async function fetchTwilioToken(ct: string | null): Promise<string> {
  if (!ct) {
    throw new JoinError('invalidLink');
  }

  const url = `${getTokenEndpoint()}?${new window.URLSearchParams({ ct })}`;

  let res: Response;
  try {
    res = await fetch(url);
  } catch (error) {
    throw new JoinError('unavailable');
  }

  if (!res.ok) {
    throw new JoinError(STATUS_ERRORS[res.status] || 'unavailable');
  }

  const token = parseToken(await res.text());
  if (!token) {
    throw new JoinError('unavailable');
  }
  return token;
}

// Accepts the JWT as a bare string, a JSON string, or inside the API's usual JSON envelope.
function parseToken(body: string) {
  let token: unknown = body.trim();
  try {
    const json = JSON.parse(body);
    token = typeof json === 'string' ? json : json?.token ?? json?.data?.token ?? json?.data;
  } catch (error) {
    // Not JSON: the body is the token itself.
  }
  return typeof token === 'string' && JWT_PATTERN.test(token) ? token : null;
}
